import AddIcon from '@mui/icons-material/Add';
import { Button, Stack } from '@mui/material';
import { type GridApiPro, GridRow } from '@mui/x-data-grid-pro';
import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';

import type { GetContractsResponse } from '@vy/api-definition';
import { getSelectorAttribute } from '@vy/app-selectors';
import { CONTRACT_FILTERABLE_FIELDS } from '@vy/contracts-types';
import { isEmptyArray, ValueOf, type ItemOf } from '@vy/utils';

import { useDialog } from '../../../../hooks/use-dialog';
import { PlanCreationOptionsDialog } from '../../components/PlanCreationOptionsDialog';
import { VyTableWithFilters } from '../../../../components/new-design/table/vy-table-with-filters';
import type { VyTableMinimalProps } from '../../../../components/new-design/table/vy-table/vy-table-types';
import { createCustomFieldColumns } from '../../../../utils/create-custom-field-columns';
import { extractCustomFieldNamesFromConfig } from '@vy/custom-fields-display';
import { extractUniqueCustomFieldNames } from '../../../../utils/extract-unique-custom-field-names';
import { CONTRACTS_COLUMNS, CONTRACTS_COLUMNS_KEYS } from '../plans-page-consts/contracts-columns';
import { ContractRow, isPendingExtractionRow, PendingExtractionRow } from '../plans-page-utils/pending-extraction-contract-row';
import { useContractsActions } from '../plans-page-hooks/use-contracts-actions';
import { useContractsFilterFields } from '../plans-page-hooks/use-contracts-filter-fields';
import { useContractsFilters } from '../plans-page-hooks/use-contracts-filters';

type ContractsTableProps = VyTableMinimalProps<ItemOf<GetContractsResponse['contracts']>> & {
  apiRef: React.MutableRefObject<GridApiPro>;
  filters: ReturnType<typeof useContractsFilters>;
  renderActions?: () => React.ReactNode;
  showSearch?: boolean;
  excludedColumns?: ValueOf<typeof CONTRACTS_COLUMNS_KEYS>[];
  disableExport?: boolean;
  disableFilter?: boolean;
};

export const ContractsTable: React.FC<ContractsTableProps> = ({
  apiRef,
  filters,
  renderActions = defaultRenderActions,
  showSearch = true,
  excludedColumns = [],
  disableExport = false,
  disableFilter = false,
  ...props
}) => {
  const dynamicFilterFields = useContractsFilterFields();

  const { handleExportSelectedContracts } = useContractsActions({
    selectedContracts: [],
    apiRef,
  });

  const columns = useMemo(() => {
    const baseColumns = CONTRACTS_COLUMNS.filter(
      (col) => !excludedColumns.includes(col.field as ValueOf<typeof CONTRACTS_COLUMNS_KEYS>),
    );
    const baseFields = new Set(CONTRACTS_COLUMNS.map((col) => col.field));
    const configCustomNames = extractCustomFieldNamesFromConfig([], baseFields);

    const dataCustomNames = extractUniqueCustomFieldNames(
      (props.items ?? []).map((item) => item.customFieldValues),
    );

    const customFieldNames = [...new Set([...configCustomNames, ...dataCustomNames])];

    if (isEmptyArray(customFieldNames)) {
      return baseColumns;
    }

    const customFieldColumns =
      createCustomFieldColumns<ItemOf<GetContractsResponse['contracts']>>(customFieldNames);

    return [...baseColumns, ...customFieldColumns];
  }, [props.items, excludedColumns]);

  const filterFields = useMemo(() => {
    return dynamicFilterFields.filter(
      (field) => !excludedColumns.includes(field.id as ValueOf<typeof CONTRACTS_COLUMNS_KEYS>),
    );
  }, [excludedColumns, dynamicFilterFields]);

  return (
    <VyTableWithFilters
      apiRef={apiRef}
      columns={columns}
      search={
        showSearch
          ? {
            value: filters.inputValue ?? '',
            onChange: filters.handleSearchInputChange,
            placeholder: 'Search by Customer, Contract, etc...',
          }
          : undefined
      }
      filters={
        disableFilter
          ? undefined
          : {
            fields: filterFields,
            onFiltersChange: filters.handleFilterClick,
            defaultFilters: filters.filters ?? [],
          }
      }
      columnSettings={{
        excludedFields: [CONTRACTS_COLUMNS_KEYS.ACTION_MENU, CONTRACTS_COLUMNS_KEYS.ID],
        mandatoryColumnsCount: 2,
      }}
      tableColumnsApiConfig={{
        visibility: {
          [CONTRACTS_COLUMNS_KEYS.CREATED_AT]: false,
          [CONTRACT_FILTERABLE_FIELDS.UPDATED_AT]: false,
        },
      }}
      onDownload={disableExport ? undefined : handleExportSelectedContracts}
      isFilterOpen={filters.isFilterContractsOpen}
      actions={renderActions()}
      disableRowSelectionOnClick
      rowsPerPageOptions={[10, 25, 50, 100, 500]}
      disableColumnMenu
      getRowId={(row) => row.id}
      slots={{
        row: (rowProps) => {
          const contract = rowProps.row as ContractRow | PendingExtractionRow;

          if (isPendingExtractionRow(contract)) {
            return <GridRow {...rowProps} />;
          }

          return (
            <NavLink to={`/plans/contracts/${contract.id}`}>
              <GridRow {...rowProps} />
            </NavLink>
          );
        },
      }}
      {...props}
    />
  );
};

function ContractsTableCreateButton() {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();

  return (
    <>
      <Stack direction="row" sx={{
        alignItems: "flex-end"
      }}>
        <Button variant="contained" onClick={handleOpen} {...getSelectorAttribute('create-contract-btn')}>
          <AddIcon />
          Create
        </Button>
      </Stack>

      <PlanCreationOptionsDialog isOpen={isDialogOpen} onClose={handleClose} />
    </>
  );
}

function defaultRenderActions() {
  return <ContractsTableCreateButton />;
}
